import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { useCategories } from "@/contexts/CategoriesContext";

export const KIND_LABELS = {
  income: "Income", expense: "Expenditure", transfer: "Transfer", investment: "Investment",
};

/** Create/edit a category. Pass `editing={id,name,kind}` for edit mode.
    Calls onSaved(name, kind) after a successful create. */
export function AddCategoryDialog({ defaultKind = "expense", editing = null, onClose, onSaved }) {
  const { addCategory, updateCategory } = useCategories();
  const [name, setName] = useState(editing?.name || "");
  const [kind, setKind] = useState(editing?.kind || defaultKind);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const save = async (e) => {
    e.preventDefault();
    // Portals bubble React synthetic events through the COMPONENT tree — without this,
    // submitting the dialog would also submit any ancestor form (e.g. the transaction modal).
    e.stopPropagation();
    const trimmed = name.trim();
    if (!trimmed) { setError("Please enter a category name."); return; }
    setBusy(true);
    setError("");
    try {
      if (editing) {
        await updateCategory(editing.id, trimmed, kind);
        toast.success(`Category updated`);
      } else {
        await addCategory(trimmed, kind);
        toast.success(`Category "${trimmed}" added`);
        onSaved?.(trimmed, kind);
      }
      onClose();
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not save the category.");
    } finally {
      setBusy(false);
    }
  };

  // Portal to <body>: the dialog's <form> must never be a DOM descendant of an
  // ancestor <form> (nested forms are invalid HTML — the browser strips them and
  // the Save button would submit the outer form instead).
  return createPortal(
    <div className="fixed inset-0 bg-[#1A1F16]/70 z-[80] flex items-center justify-center p-6" onClick={(e) => { e.stopPropagation(); onClose(); }} data-testid="add-category-dialog">
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-[#F7F5F0] border border-[#1A1F16] p-8 w-full max-w-sm">
        <h3 className="font-serif text-3xl tracking-tight mb-6">{editing ? "Edit category." : "New category."}</h3>

        <label className="block mb-5">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Category type</span>
          <select
            value={kind}
            onChange={(e) => setKind(e.target.value)}
            data-testid="new-category-kind"
            className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none"
          >
            {Object.entries(KIND_LABELS).map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </label>

        <label className="block mb-5">
          <span className="font-mono text-[10px] tracking-widest uppercase text-[#6C7565] block mb-1">Category name</span>
          <input
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={40}
            placeholder="e.g. Pet care"
            data-testid="new-category-name"
            className="w-full bg-transparent border-b border-[#1A1F16] py-2 focus:outline-none focus:border-[#A67C00]"
          />
        </label>

        {error && <p className="text-xs text-[#D94F3D] mb-4" data-testid="new-category-error">{error}</p>}

        <div className="flex gap-3">
          <button type="button" onClick={onClose} data-testid="new-category-cancel" className="flex-1 border border-[#1A1F16] py-3 text-xs uppercase tracking-widest hover:bg-[#EAE7DD]">Cancel</button>
          <button type="submit" disabled={busy} data-testid="new-category-save" className="flex-1 bg-[#1A1F16] text-[#F7F5F0] py-3 text-xs uppercase tracking-widest hover:bg-[#2C3628] disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>,
    document.body
  );
}
